import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { UserService } from "@/services/userService";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { MoreHorizontal, Shield, UserCheck, Plus, Trash2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import type { Database } from "@/integrations/supabase/types";

type AppRole = Database["public"]["Enums"]["app_role"];

interface UserWithRole {
  user_id: string;
  full_name: string;
  email: string;
  roles: AppRole[];
}

const roleLabel = (role: AppRole) => {
  switch (role) {
    case "admin": return "Admin";
    case "aca_member": return "ACA Member";
    case "growlearn": return "GrowLearn";
    case "student": return "Student";
  }
};

const roleBadgeVariant = (role: AppRole) => {
  switch (role) {
    case "admin": return "default" as const;
    case "aca_member": return "default" as const;
    case "growlearn": return "secondary" as const;
    case "student": return "outline" as const;
  }
};

const ManageUsers = () => {
  const [users, setUsers] = useState<UserWithRole[]>([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUser, setNewUser] = useState({ full_name: "", email: "", password: "", role: "" });
  const { toast } = useToast();
  const { user: currentUser } = useAuth();

  const allowedRoles = [
    { value: "student", label: "Student" },
    { value: "growlearn", label: "GrowLearn" },
    { value: "aca_member", label: "ACA Member" },
  ];

  const fetchUsers = async () => {
    setLoading(true);
    const { data: profiles } = await supabase.from("profiles").select("user_id, full_name, email");
    const { data: roles } = await supabase.from("user_roles").select("user_id, role");
    if (profiles && roles) {
      const roleMap = new Map<string, AppRole[]>();
      roles.forEach((r) => {
        const existing = roleMap.get(r.user_id) || [];
        existing.push(r.role);
        roleMap.set(r.user_id, existing);
      });
      setUsers(profiles.map((p) => ({ user_id: p.user_id, full_name: p.full_name, email: p.email, roles: roleMap.get(p.user_id) || ["student"] })));
    }
    setLoading(false);
  };

  useEffect(() => { fetchUsers(); }, []);

  const isCurrentUser = (userId: string) => currentUser?.id === userId;

  const assignRole = async (userId: string, role: AppRole) => {
    const { error } = await supabase.from("user_roles").insert({ user_id: userId, role });
    if (error) {
      toast({ title: error.code === "23505" ? "Already assigned" : "Error", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Role assigned", description: `${roleLabel(role)} role assigned.` });
    fetchUsers();
  };

  const removeRole = async (userId: string, role: AppRole) => {
    if (role === "admin") { toast({ title: "Cannot remove", description: "Admin role cannot be removed.", variant: "destructive" }); return; }
    const { error } = await supabase.from("user_roles").delete().eq("user_id", userId).eq("role", role);
    if (error) { toast({ title: "Error", description: error.message, variant: "destructive" }); return; }
    const { data: remaining } = await supabase.from("user_roles").select("role").eq("user_id", userId);
    if (!remaining || remaining.length === 0) await supabase.from("user_roles").insert({ user_id: userId, role: "student" });
    toast({ title: "Role removed" });
    fetchUsers();
  };

  const removeAllStaffRoles = async (userId: string) => {
    if (isCurrentUser(userId)) { toast({ title: "Cannot remove yourself", variant: "destructive" }); return; }
    await supabase.from("user_roles").delete().eq("user_id", userId).neq("role", "student");
    const { data: hasStudent } = await supabase.from("user_roles").select("id").eq("user_id", userId).eq("role", "student").maybeSingle();
    if (!hasStudent) await supabase.from("user_roles").insert({ user_id: userId, role: "student" });
    toast({ title: "User demoted to student" });
    fetchUsers();
  };

  const handleCreateUser = async () => {
    if (!newUser.full_name || !newUser.email || !newUser.password || !newUser.role) {
      toast({ title: "Missing fields", variant: "destructive" }); return;
    }
    setCreating(true);
    try {
      await UserService.create({ email: newUser.email, password: newUser.password, full_name: newUser.full_name, role: newUser.role as any });
      toast({ title: "User created", description: `${newUser.full_name} created as ${roleLabel(newUser.role as AppRole)}.` });
      setCreateOpen(false);
      setNewUser({ full_name: "", email: "", password: "", role: "" });
      fetchUsers();
    } catch (err: any) {
      toast({ title: "Error", description: err.response?.data?.error || err.message, variant: "destructive" });
    }
    setCreating(false);
  };

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in">
        <div className="rounded-2xl gradient-hero p-8 text-primary-foreground flex items-center justify-between shadow-xl">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Manage Users</h2>
            <p className="mt-1.5 text-primary-foreground/70 text-sm">Create users and manage role assignments</p>
          </div>
          <Dialog open={createOpen} onOpenChange={setCreateOpen}>
            <DialogTrigger asChild>
              <Button className="gradient-warm text-white border-0 shadow-lg hover:opacity-90 transition-opacity">
                <Plus className="mr-2 h-4 w-4" /> Create User
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-[95vw] sm:max-w-md">
              <DialogHeader><DialogTitle>Create New User</DialogTitle></DialogHeader>
              <div className="space-y-4 pt-4">
                <div className="space-y-2"><Label>Full Name</Label><Input value={newUser.full_name} onChange={(e) => setNewUser({ ...newUser, full_name: e.target.value })} placeholder="John Doe" /></div>
                <div className="space-y-2"><Label>Email</Label><Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} placeholder="john@example.com" /></div>
                <div className="space-y-2"><Label>Password</Label><Input type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} placeholder="Min 6 characters" /></div>
                <div className="space-y-2">
                  <Label>Role</Label>
                  <Select value={newUser.role} onValueChange={(val) => setNewUser({ ...newUser, role: val })}>
                    <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
                    <SelectContent>
                      {allowedRoles.map((r) => (<SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <Button className="w-full gradient-warm text-white border-0" onClick={handleCreateUser} disabled={creating}>{creating ? "Creating..." : "Create User"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="glass-premium rounded-xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-12"><div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
          ) : (
            <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/30">
                  <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Roles</TableHead><TableHead className="w-[100px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.user_id} className="hover:bg-accent/50 transition-colors">
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full gradient-primary flex items-center justify-center text-xs font-bold text-primary-foreground">
                          {u.full_name ? u.full_name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) : "?"}
                        </div>
                        <span>{u.full_name || "—"}</span>
                        {isCurrentUser(u.user_id) && <Badge variant="outline" className="text-xs">You</Badge>}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{u.email}</TableCell>
                    <TableCell><div className="flex flex-wrap gap-1">{u.roles.map((r) => <Badge key={r} variant={roleBadgeVariant(r)}>{roleLabel(r)}</Badge>)}</div></TableCell>
                    <TableCell>
                      {!isCurrentUser(u.user_id) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {!u.roles.includes("student") && <DropdownMenuItem onClick={() => assignRole(u.user_id, "student")}><UserCheck className="mr-2 h-4 w-4" />Assign Student</DropdownMenuItem>}
                            {!u.roles.includes("growlearn") && <DropdownMenuItem onClick={() => assignRole(u.user_id, "growlearn")}><UserCheck className="mr-2 h-4 w-4" />Assign GrowLearn</DropdownMenuItem>}
                            {!u.roles.includes("aca_member") && <DropdownMenuItem onClick={() => assignRole(u.user_id, "aca_member")}><Shield className="mr-2 h-4 w-4" />Assign ACA Member</DropdownMenuItem>}
                            {u.roles.includes("growlearn") && <DropdownMenuItem onClick={() => removeRole(u.user_id, "growlearn")} className="text-destructive">Remove GrowLearn</DropdownMenuItem>}
                            {u.roles.includes("aca_member") && <DropdownMenuItem onClick={() => removeRole(u.user_id, "aca_member")} className="text-destructive">Remove ACA Member</DropdownMenuItem>}
                            {(u.roles.includes("growlearn") || u.roles.includes("aca_member")) && (
                              <DropdownMenuItem onClick={() => removeAllStaffRoles(u.user_id)} className="text-destructive"><Trash2 className="mr-2 h-4 w-4" />Demote to Student</DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ManageUsers;
